/**
 * Building an Intl.DateTimeFormat is expensive, so formatters are built once
 * per zone and reused — same pattern as ProjectTimezoneSettings.tsx. Only the
 * formatter is cached, never the result, so DST transitions still resolve
 * correctly per call.
 */
const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (timezone: string): Intl.DateTimeFormat | null => {
  const cached = formatters.get(timezone);
  if (cached) return cached;

  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    formatters.set(timezone, formatter);
    return formatter;
  } catch {
    // Unusable timezone — not cached, so a later valid value still gets a chance.
    return null;
  }
};

/**
 * Formats an already-timezone-converted ISO datetime plus its IANA zone name
 * into "2026-07-27 04:33 PM PST"-style display text.
 *
 * Renders `createdAt` in `timezone` — it does NOT reinterpret the instant in
 * the browser's local zone, unlike `new Date(...).toLocaleString()`. The
 * backend already performs the timezone conversion; this only formats it.
 *
 * @param createdAt ISO datetime string, already expressed in `timezone`.
 * @param timezone IANA timezone name, e.g. "America/Los_Angeles".
 */
export const formatProjectDateTime = (createdAt: string, timezone: string): string => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return createdAt;

  const formatter = getFormatter(timezone);
  if (!formatter) return createdAt;

  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value || '';

  const year = get('year');
  const month = get('month');
  const day = get('day');
  const hour = get('hour');
  const minute = get('minute');
  const dayPeriod = get('dayPeriod').toUpperCase();
  const timeZoneName = get('timeZoneName');

  return `${year}-${month}-${day} ${hour}:${minute} ${dayPeriod} ${timeZoneName}`.trim();
};
